import React, { ReactNode } from 'react';
import PermissionConfigurable from './PermissionConfigurable';
import { useTryPermissionsMode } from '../contexts/PermissionsModeContext';
import { useTryPermissions } from '../contexts/PermissionContext';
import { PermissionAccessProvider } from '../contexts/PermissionAccessContext';

interface PermissionsContainerProps {
  componentId?: string;
  componentName: string;
  children: ReactNode;
  className?: string;
  defaultHidden?: boolean;
  /**
   * When true (and `defaultHidden` is false), un-configured permissions
   * default to access level 1 (view-only / disabled inputs) instead of the
   * usual level 2 (full edit). Use for features that should ship behind a
   * permission gate but still surface their values to all users — e.g. a
   * controlled rollout where the data is safe to show but the controls
   * should stay locked until an admin explicitly grants edit access.
   *
   * Ignored when `defaultHidden` is true (hidden wins).
   */
  defaultDisabled?: boolean;
  /**
   * If false, the permissions frame (purple outline + handle) is suppressed even in
   * permissions mode. Use for repeated instances sharing the same componentId so only
   * one instance is decorated.
   */
  showHandle?: boolean;
  /** @deprecated Alias for `showHandle` — kept for existing call sites. */
  showCog?: boolean;
}

const PermissionsContainer: React.FC<PermissionsContainerProps> = ({
  componentId,
  componentName,
  children,
  className = '',
  defaultHidden = false,
  defaultDisabled = false,
  showHandle,
  showCog = true,
}) => {
  const finalComponentId = componentId || `container-${componentName.toLowerCase().replace(/\s+/g, '-')}`;
  const accessLevel = useResolvedAccessLevel(finalComponentId, defaultHidden, defaultDisabled);
  // Lenient lookup — when no provider is in the tree (tests, previews),
  // we skip PermissionConfigurable entirely and pass children through with
  // the resolved (fallback) access level. PermissionConfigurable uses the
  // strict hooks internally and requires both providers to render.
  const modeCtx = useTryPermissionsMode();
  const permissionsMode = modeCtx?.permissionsMode ?? false;
  const hasProviders = !!modeCtx;

  if (permissionsMode) {
    return (
      <PermissionConfigurable
        componentId={finalComponentId}
        componentName={componentName}
        className={className}
        showHandle={showHandle ?? showCog}
      >
        {children}
      </PermissionConfigurable>
    );
  }

  if (accessLevel === 0) return null;

  // Without providers (test/preview trees), skip the configurable wrapper.
  if (!hasProviders) {
    return (
      <PermissionAccessProvider accessLevel={accessLevel}>
        {children}
      </PermissionAccessProvider>
    );
  }

  return (
    <PermissionAccessProvider accessLevel={accessLevel}>
      <PermissionConfigurable
        componentId={finalComponentId}
        componentName={componentName}
        className={className}
        showHandle={showHandle ?? showCog}
      >
        {children}
      </PermissionConfigurable>
    </PermissionAccessProvider>
  );
};

export const useResolvedAccessLevel = (
  componentId: string,
  defaultHidden: boolean = false,
  defaultDisabled: boolean = false,
  /**
   * Explicit level for an un-granted component, overriding the
   * hidden/disabled/edit ladder. Needed when a component has to tell
   * "no grant" apart from "granted Edit" — both are 2 under the ladder.
   * `lc-return-target-date` defaults to Exempt (3) so that an explicit Edit
   * grant, and only that, makes its date field mandatory.
   */
  defaultLevel?: number,
): number => {
  // Lenient context lookups so the container works in test trees and
  // isolated previews that haven't wrapped the providers.
  const modeCtx = useTryPermissionsMode();
  const permsCtx = useTryPermissions();

  const componentPermissions = modeCtx?.componentPermissions;
  const componentAccessLevels = modeCtx?.componentAccessLevels;
  const permissions = permsCtx?.permissions;

  // Default access when no explicit permission grants exist for this
  // component. An explicit defaultLevel wins; otherwise hidden > disabled > edit.
  const fallback = defaultLevel ?? (defaultHidden ? 0 : defaultDisabled ? 1 : 2);

  // Authoritative source: the server-resolved effective level for *this* user
  // (GET /permissions/components/access-levels — highest-wins across their groups
  // plus any user-specific grant). Only components with a grant that applies to
  // the caller appear in the map, so a miss means "no grant" → fallback.
  if (componentAccessLevels) {
    const effective = componentAccessLevels[componentId];
    return effective === undefined ? fallback : effective;
  }

  // The effective map is unavailable (still loading, or no provider in the tree
  // as in tests/previews). Fall back to resolving the full component_permissions
  // dump client-side — the historical behavior, kept so gating degrades to the
  // previous semantics rather than flashing every field open.
  const currentPermissions = componentPermissions?.get(componentId);

  if (!currentPermissions || Object.keys(currentPermissions.permissions).length === 0) {
    return fallback;
  }

  const userGroups = permissions?.groups || [];
  const userId = permissions?.user_id;

  let maxLevel = -1;

  userGroups.forEach(groupId => {
    const groupPerms = currentPermissions.permissions[groupId];
    if (groupPerms && groupPerms.entityType === 'group') {
      maxLevel = Math.max(maxLevel, groupPerms.accessLevel);
    }
  });

  if (userId && currentPermissions.permissions[userId]) {
    const userPerms = currentPermissions.permissions[userId];
    if (userPerms.entityType === 'user') {
      maxLevel = Math.max(maxLevel, userPerms.accessLevel);
    }
  }

  if (maxLevel === -1) {
    return fallback;
  }

  return maxLevel;
};

/** @deprecated Use useComponentAccessLevel() from PermissionAccessContext instead */
export const useHasViewPermission = (componentId: string): boolean => {
  const permissionsMode = useTryPermissionsMode()?.permissionsMode ?? false;
  if (permissionsMode) return true;
  const accessLevel = useResolvedAccessLevel(componentId);
  return accessLevel >= 1;
};

export default PermissionsContainer;
